import React, { useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import StandingsSection from './StandingsSection.jsx';

const STANDINGS_TABS = [
  { key: 'general', title: 'GÉNÉRAL', shortLabel: 'Général' },
  { key: 'home', title: 'DOMICILE', shortLabel: 'Domicile' },
  { key: 'away', title: 'EXTÉRIEUR', shortLabel: 'Extérieur' },
];

export default function StandingsWidget({
  discipline = 'rugby',
  competitionIds = [],
  competitionInfos = [],
  day = null,
  onNavigate,
  widgetConfigurationId=null,
  title,
  showCompetitionInfos=true,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tabs = useMemo(() => STANDINGS_TABS, []);
  const [activeTab, setActiveTab] = useState(0);

  const currentTab = tabs[activeTab];

  if (!competitionIds.length) return null;

  const isMultiCompetition = competitionIds.length > 1;

  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack spacing={2.5}>
          {title ? (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: '#1f1f1f',
              }}
            >
              {title}
            </Typography>
          ) : null}

          { showCompetitionInfos && competitionInfos.length > 0 ? (
            <Stack spacing={1}>
              {competitionInfos.map((competition) => (
                <Box key={competition.id || competition.name}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: '#374151',
                      lineHeight: 1.3,
                    }}
                  >
                    {competition.name}
                  </Typography>

                  {competition.season ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.25 }}
                    >
                      Saison {competition.season}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          ) : null}

          <Box
            sx={{
              borderBottom: '1px solid #e5e7eb',
              overflowX: 'auto',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                style: { display: 'none' },
              }}
              sx={{
                minHeight: 0,
                '& .MuiTabs-flexContainer': {
                  gap: 1,
                },
              }}
            >
              {tabs.map((tab, index) => {
                const selected = index === activeTab;

                return (
                  <Tab
                    key={tab.key}
                    disableRipple
                    label={isMobile ? tab.shortLabel : tab.title}
                    sx={{
                      minHeight: 28,
                      px: 1.25,
                      py: 0.25,
                      borderRadius: '999px',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      fontSize: { xs: 10, md: 11 },
                      minWidth: 'auto',
                      color: selected ? '#111827' : '#6b7280',
                      bgcolor: selected ? '#e5f2e6' : 'transparent',
                    }}
                  />
                );
              })}
            </Tabs>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            {isMultiCompetition ? (
              <Typography variant="body2" color="text.secondary">
                Classement cumulé - toutes journées confondues
              </Typography>
            ) : day != null ? (
              <Typography variant="body2" color="text.secondary">
                Journée {day}
              </Typography>
            ) : null}
          </Stack>

          {currentTab ? (
            <StandingsSection
              discipline={discipline}
              competitionIds={competitionIds}
              day={day}
              statType={currentTab.key}
              widgetConfigurationId={widgetConfigurationId}
              onNavigate={onNavigate}
            />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}